import { zodResolver } from "@hookform/resolvers/zod";
import {
  Accordion,
  AccordionDetails,
  AccordionGroup,
  AccordionSummary,
  Alert,
  Avatar,
  Box,
  Card,
  CardContent,
  CardOverflow,
  Link,
  Stack,
  Typography,
} from "@mui/joy";
import { ApiChallenge } from "griptonite-api-client";
import { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";

import AppIcon from "@/assets/icons/AppIcon";
import Warning from "@/assets/icons/Warning";
import { useChallengeQuery, useUpdateChallengeMutation } from "@/hooks/challenges";
import { useAllVenuesByUuid, useCurrentVenueQuery } from "@/hooks/venues";
import { handleFormError } from "@/utils/handleFormError";

import FormErrorAlert from "../FormErrorAlert";
import CheckboxInput from "../forms/CheckboxInput";
import { MediaItemPreview, MediaSelectInput } from "../forms/MediaInput";
import SelectInput from "../forms/SelectInput";
import TextInput, { TextareaInput } from "../forms/TextInput";
import BottomButtons from "./BottomButtons";
import {
  ChallengePromotionFormSchema,
  ChallengePromotionFormValues,
  challengePromotionFormValuesToPayload,
  challengeToPromotionFormValues,
} from "./forms";
import { useUnhidePreviousTabWarningsOnMount } from "./getPublishWarnings";

const NeedsIntegrationOverlay = () => (
  <Box
    sx={{
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      zIndex: 2,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "#ffffffdd",
    }}
  >
    <Typography
      level="body-lg"
      textAlign="center"
      padding={2}
      sx={{
        textShadow: "0 0 8px white, 0 0 20px white",
      }}
    >
      Griptonite integrates with your front desk software to give users more efficient checkins.{" "}
      <Link href="mailto:support@griptonite.io">Contact support</Link> to get set up.
    </Typography>
  </Box>
);

const NotificationPreview = ({
  name,
  title,
}: {
  name: keyof ChallengePromotionFormValues["notifications"];
  title: string;
}) => {
  const { watch } = useFormContext<ChallengePromotionFormValues>();
  let text = watch(`notifications.${name}.text`);

  if (name === "challengeEndingSoon") {
    text = (text || "").replace("{{COUNT}}", "3");
  }

  return (
    <Stack position="relative">
      {/* <Typography level="body-sm" textColor="neutral.600">
        Preview
      </Typography> */}
      <Stack
        spacing={1}
        padding={1.5}
        direction="row"
        sx={{
          backgroundColor: "#0009",
          borderRadius: 16,
          color: "white",
          boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
        }}
      >
        <AppIcon sx={{ width: 36, height: 36 }} />
        <Box>
          <Typography
            fontSize="13px"
            fontWeight="600"
            fontFamily="system-ui, sans-serif"
            textColor="white"
            lineHeight="1.2"
            marginBottom={0.25}
          >
            {title}
          </Typography>
          <Typography
            level="body-sm"
            fontSize="13px"
            fontFamily="system-ui, sans-serif"
            lineHeight="1.2"
            textColor="white"
          >
            {text || "(No message configured)"}
          </Typography>
        </Box>
      </Stack>
    </Stack>
  );
};

const NotificationCard = ({
  challenge,
  name,
  title,
  description,
  previewTitle,
  disabled,
}: {
  challenge?: ApiChallenge;
  name: keyof ChallengePromotionFormValues["notifications"];
  title: string;
  description: string;
  previewTitle: string;
  disabled?: boolean;
}) => {
  const { watch } = useFormContext<ChallengePromotionFormValues>();
  const enabled = watch(`notifications.${name}.enabled`);
  useCurrentVenueQuery();

  const allVenuesByUuid = useAllVenuesByUuid();

  const venuesWithoutCheckinProvider = useMemo(() => {
    return challenge?.venues.filter((v) => allVenuesByUuid?.[v.uuid]?.checkinProvider === null);
  }, [allVenuesByUuid, challenge]);
  const showNeedsIntegrationOverlay =
    name === "checkinPromo" &&
    venuesWithoutCheckinProvider &&
    venuesWithoutCheckinProvider.length === challenge?.venues.length;
  const showNeedsIntegrationWarning =
    name === "checkinPromo" &&
    venuesWithoutCheckinProvider &&
    !!venuesWithoutCheckinProvider.length;

  return (
    <Card sx={{ minWidth: 200, position: "relative", overflow: "hidden" }}>
      {showNeedsIntegrationOverlay ? <NeedsIntegrationOverlay /> : null}
      <CardContent>
        <Typography level="title-md" marginBottom={0}>
          {title}
        </Typography>
        <NotificationPreview name={name} title={previewTitle} />
        {description && (
          <Typography level="body-sm" textColor="neutral.600" marginTop={0.5}>
            {description}
          </Typography>
        )}

        <Stack spacing={2} sx={{ marginTop: 1.5 }}>
          <Box>
            <CheckboxInput
              name={`notifications.${name}.enabled`}
              label="Enabled"
              disabled={disabled}
            />
          </Box>
          <TextareaInput
            name={`notifications.${name}.text`}
            label="Message"
            minRows={2}
            maxRows={3}
            disabled={!enabled || disabled}
          />
        </Stack>
      </CardContent>
      {showNeedsIntegrationWarning ? (
        <Alert
          startDecorator={<Warning />}
          color="warning"
          sx={{
            ".MuiAlert-startDecorator": {
              alignSelf: "flex-start",
              marginTop: "2px",
            },
          }}
        >
          Some gyms do not have a checkin integration:
          <br />
          {venuesWithoutCheckinProvider.map((v) => v.name).join(", ")}
        </Alert>
      ) : null}
    </Card>
  );
};

const NotificationsForm = ({ challenge }: { challenge?: ApiChallenge }) => {
  const { data: currentVenue } = useCurrentVenueQuery();
  const startPreviewTitle = currentVenue ? currentVenue.name : "Challenge starting!";
  return (
    <Box
      sx={{
        display: "grid",
        gap: 2,
        gridTemplateColumns: "1fr 1fr",
      }}
    >
      <NotificationCard
        name="challengeStart"
        title="Starting notification"
        previewTitle={startPreviewTitle}
        description="Sent to recent visitors to promote the challenge just after it starts"
        disabled={challenge?.startTime ? new Date() > new Date(challenge.startTime) : false}
      />
      <NotificationCard
        name="checkinPromo"
        title="Check-in promo notification"
        description="Sent when a non-participating customer arrives at the gym and checks in"
        previewTitle={`Welcome to ${currentVenue?.name || "the gym"}!`}
        challenge={challenge}
      />
      <NotificationCard
        name="challengeEndingSoon"
        title="Ending soon notification"
        description="Sent a few days before the challenge ends to let participants know the challenge is ending soon"
        previewTitle={`${challenge?.name || "Challenge"} ending soon!`}
      />
      <NotificationCard
        name="participationThanks"
        title="Participation thanks notification"
        description="If enabled, participants will receive a thank you message after completing the challenge. You could use this to give them an extra giveaway or discount."
        previewTitle={`${challenge?.name || "Challenge"}`}
      />
    </Box>
  );
};

const TvDisplayForm = () => {
  const { watch } = useFormContext<ChallengePromotionFormValues>();
  const frequency = watch("tvTakeover.frequency");

  const frequencyOptions = [
    { name: "Low", value: "LOW", description: "Shown about 2 times per hour" },
    {
      name: "Medium",
      value: "MEDIUM",
      description: "Shown about 4 times per hour",
    },
    {
      name: "High",
      value: "HIGH",
      description: "Shown about 12 times per hour",
    },
  ];
  const freqDescription = frequencyOptions.find((opt) => opt.value === frequency)?.description;

  return (
    <Stack spacing={1}>
      <Box>
        <CheckboxInput name="tvTakeover.enabled" label="Enable TV ad for this challenge" />
      </Box>

      <MediaSelectInput
        name="tvTakeover.media"
        label="Media (image or video)"
        accept="image/*,video/*"
        multiple={false}
        helpText="Upload an image or video that will be displayed on gym TV screens to promote this challenge."
      />

      <SelectInput
        name="tvTakeover.frequency"
        label="Display frequency"
        options={frequencyOptions}
      />

      <Typography level="body-sm" textColor="neutral.600">
        {freqDescription}
      </Typography>
    </Stack>
  );
};

const FeedCardPreview = ({ challenge }: { challenge: ApiChallenge | undefined }) => {
  const { watch } = useFormContext<ChallengePromotionFormValues>();
  const headline = watch("feedCard.headline");
  const postText = watch("feedCard.postText");
  const image = watch("feedCard.image") || challenge?.coverImage;
  const { data: venue } = useCurrentVenueQuery();

  return (
    <Card sx={{ maxWidth: 400, minWidth: 320, alignSelf: "flex-start" }}>
      <CardContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          flex: "0",
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Avatar
            src={venue?.logo?.url || ""}
            size="md"
            sx={{
              borderRadius: 4,
              borderColor: "neutral.200",
              borderWidth: 1,
              borderStyle: "solid",
            }}
          />
          <Box>
            <Typography fontWeight="bold" level="body-sm">
              {venue?.name || "Gym"}
            </Typography>
            <Typography level="body-sm" textColor="neutral.600">
              Just now
            </Typography>
          </Box>
        </Stack>
        <Typography level="body-sm">{postText || challenge?.description}</Typography>
      </CardContent>
      <CardOverflow
        sx={{
          background:
            "radial-gradient(91.33% 55.37% at 8.67% 7.9%, #1E1C54 0%, #161541 100%) /* warning: gradient uses a rotation that is not supported by CSS and may not behave as expected */",
          paddingBottom: 2,
        }}
      >
        <Typography
          fontSize="24px"
          textAlign="center"
          padding={2}
          textColor="white"
          fontWeight="bold"
          lineHeight={1.25}
        >
          {headline || challenge?.name}
        </Typography>
        {image ? (
          <Box sx={{ width: "100%" }}>
            <MediaItemPreview mediaItem={image} />
          </Box>
        ) : (
          <Box
            sx={{
              aspectRatio: 2,
              border: "2px solid",
              borderColor: "neutral.200",
              display: "flex",
            }}
          ></Box>
        )}
      </CardOverflow>
      <CardContent sx={{ flex: 0, alignItems: "flex-end" }}>
        <Typography level="body-sm" textColor="neutral.600" marginBottom={1}>
          0 comments
        </Typography>
        <FakeButtons />
      </CardContent>
    </Card>
  );
};

const FakeButtons = () => (
  <svg width="88" height="40" viewBox="0 0 88 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="40" height="40" rx="20" fill="#F1F1F4" />
    <path
      d="M28.791 28.75L25.5202 25.4791H13.1243C12.6105 25.4791 12.166 25.2916 11.791 24.9166C11.416 24.5416 11.2285 24.0972 11.2285 23.5833V13.1458C11.2285 12.6319 11.416 12.184 11.791 11.802C12.166 11.4201 12.6105 11.2291 13.1243 11.2291H26.8743C27.4021 11.2291 27.8535 11.4201 28.2285 11.802C28.6035 12.184 28.791 12.6319 28.791 13.1458V28.75Z"
      fill="#010024"
    />
    <rect x="48" width="40" height="40" rx="20" fill="#F1F1F4" />
    <path
      d="M73.1458 28.7708C72.3681 28.7708 71.7014 28.4896 71.1458 27.9271C70.5903 27.3646 70.3125 26.7083 70.3125 25.9583C70.3125 25.8472 70.3264 25.7188 70.3542 25.5729C70.3819 25.4271 70.4097 25.2847 70.4375 25.1458L64.875 21.8958C64.6111 22.1736 64.309 22.3993 63.9688 22.5729C63.6285 22.7465 63.2639 22.8333 62.875 22.8333C62.125 22.8333 61.4688 22.5521 60.9062 21.9896C60.3438 21.4271 60.0625 20.7639 60.0625 20C60.0625 19.2222 60.3438 18.559 60.9062 18.0104C61.4688 17.4618 62.125 17.1875 62.875 17.1875C63.2639 17.1875 63.625 17.2604 63.9583 17.4062C64.2917 17.5521 64.5972 17.7639 64.875 18.0417L70.4375 14.8333C70.4097 14.7361 70.3819 14.6076 70.3542 14.4479C70.3264 14.2882 70.3125 14.1597 70.3125 14.0625C70.3125 13.2986 70.5903 12.6389 71.1458 12.0833C71.7014 11.5278 72.3681 11.25 73.1458 11.25C73.9097 11.25 74.5694 11.5278 75.125 12.0833C75.6806 12.6389 75.9583 13.2986 75.9583 14.0625C75.9583 14.8264 75.6771 15.4861 75.1146 16.0417C74.5521 16.5972 73.8958 16.875 73.1458 16.875C72.7292 16.875 72.3611 16.8194 72.0417 16.7083C71.7222 16.5972 71.4375 16.4028 71.1875 16.125L65.6042 19.1667C65.6319 19.2917 65.6562 19.4375 65.6771 19.6042C65.6979 19.7708 65.7083 19.9028 65.7083 20C65.7083 20.0972 65.6979 20.2118 65.6771 20.3438C65.6562 20.4757 65.6319 20.6042 65.6042 20.7292L71.1875 23.8542C71.4375 23.6319 71.7187 23.4549 72.0312 23.3229C72.3437 23.191 72.7153 23.125 73.1458 23.125C73.8958 23.125 74.5521 23.4062 75.1146 23.9688C75.6771 24.5312 75.9583 25.1944 75.9583 25.9583C75.9583 26.7083 75.6806 27.3646 75.125 27.9271C74.5694 28.4896 73.9097 28.7708 73.1458 28.7708Z"
      fill="#010024"
    />
  </svg>
);

const FeedCardForm = ({ challenge }: { challenge: ApiChallenge }) => {
  return (
    <Stack direction="row" spacing={2}>
      <Stack spacing={1}>
        <Box>
          <CheckboxInput name="feedCard.enabled" label="Enable feed card for this challenge" />
        </Box>
        <TextInput name="feedCard.headline" label="Custom headline" />
        <TextareaInput name="feedCard.postText" label="Custom post text" minRows={2} maxRows={4} />
        <MediaSelectInput
          name="feedCard.image"
          label="Media (image or video)"
          accept="image/*"
          multiple={false}
          helpText="Upload an image or video that will be displayed in the feed card to promote this challenge. If not set, the challenge cover image will be used"
        />
      </Stack>
      <FeedCardPreview challenge={challenge} />
    </Stack>
  );
};

const ChallengePromotionForm = ({ challengeUuid }: { challengeUuid: string }) => {
  const { data: venue } = useCurrentVenueQuery();

  const { data: challenge, isPending } = useChallengeQuery(challengeUuid, venue?.uuid);

  const defaultValues = useMemo(() => challengeToPromotionFormValues(challenge), [challenge]);

  const methods = useForm({
    mode: "onBlur",
    resolver: zodResolver(ChallengePromotionFormSchema),
    defaultValues,
  });

  useEffect(() => {
    methods.reset(defaultValues);
  }, [defaultValues, methods]);

  const updateMutation = useUpdateChallengeMutation(challenge?.uuid, venue?.uuid);

  useUnhidePreviousTabWarningsOnMount(challengeUuid, "rules");

  if (isPending) {
    return null;
  }

  const onSubmitBase = async (values: ChallengePromotionFormValues, isPublish: boolean) => {
    const touched = methods.formState.touchedFields;
    const payload = challengePromotionFormValuesToPayload(values, touched);
    await updateMutation.mutateAsync(
      {
        ...payload,
        ...(isPublish ? { isHidden: false } : {}),
      },
      {
        onError: async (error) => {
          handleFormError(error, methods);
        },
      },
    );
  };
  const onSubmit = (values: ChallengePromotionFormValues) => {
    onSubmitBase(values, false);
  };
  const onPublish = (values: ChallengePromotionFormValues) => {
    onSubmitBase(values, true);
  };

  return (
    <Stack
      sx={{ flex: 1, overflowY: "auto", minHeight: 0, padding: 1.5 }}
      spacing={1.5}
      component="form"
      autoComplete="off"
      onSubmit={methods.handleSubmit(onSubmit)}
    >
      <FormProvider {...methods}>
        <FormErrorAlert />
        <PromotionSections challenge={challenge} />
        <BottomButtons
          challenge={challenge}
          formState={methods.formState}
          isFinalStep
          onPublishClick={() => methods.handleSubmit(onPublish)()}
        />
      </FormProvider>
    </Stack>
  );
};

const PromotionSections = ({ challenge }: { challenge?: ApiChallenge }) => {
  const [expandedAccordion, setExpandedAccordion] = useState<string | null>("notifications");

  return (
    <AccordionGroup
      size="lg"
      sx={{
        flex: 1,
        overflow: "hidden",
        gap: 1,
        padding: 0,
        ".MuiAccordion-root.Mui-expanded": {
          flex: 1,
        },
        ".MuiAccordion-root": {
          border: "none",
          padding: 0,
        },
        ".MuiAccordionSummary-root": {
          marginRight: 0,
          marginLeft: 0,
          border: "none",
        },
        ".MuiAccordionDetails-root": {},
        ".MuiAccordionDetails-content.Mui-expanded": {
          overflowY: "auto",
          paddingTop: 2,
        },
        ".MuiAccordionSummary-button": {
          fontWeight: "bold",
          backgroundColor: "neutral.100",
          borderRadius: 8,
        },
      }}
    >
      <Accordion
        expanded={expandedAccordion === "notifications"}
        onChange={() => setExpandedAccordion("notifications")}
      >
        <AccordionSummary>
          <Typography level="title-md">Notifications</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <NotificationsForm challenge={challenge} />
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={expandedAccordion === "tv-display"}
        onChange={() => setExpandedAccordion("tv-display")}
      >
        <AccordionSummary>
          <Typography level="title-md">TV advertising</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TvDisplayForm />
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={expandedAccordion === "feed-card"}
        onChange={() => setExpandedAccordion("feed-card")}
      >
        <AccordionSummary>
          <Typography level="title-md">Feed card</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {challenge ? <FeedCardForm challenge={challenge} /> : null}
        </AccordionDetails>
      </Accordion>
    </AccordionGroup>
  );
};

export default ChallengePromotionForm;
